import React from 'react';
import { MapPin, CheckCircle, Globe, Target, Share2 } from 'lucide-react';

export default function ClusterMarketing() {
  const framework = [
    {
      phase: 'Phase 1: Cluster Discovery',
      duration: 'Weeks 1-3',
      activities: [
        'Data lake build combining POS, marketplace, census, and psychographic inputs',
        'Demand cluster modeling to reveal micro-markets with shared behaviors',
        'Persona narratives that tie buying triggers to geography and culture',
        'Opportunity sizing and prioritization for top 10 clusters'
      ]
    },
    {
      phase: 'Phase 2: Localized Playbooks',
      duration: 'Weeks 4-6',
      activities: [
        'Message pillars rewritten for local vernacular, needs, and proof points',
        'Offer architecture tailored to cluster economics and seasonality',
        'Channel mix design blending digital, retail, and community touchpoints',
        'Creative asset guidelines covering imagery, copy, and cultural cues'
      ]
    },
    {
      phase: 'Phase 3: Activation Sprints',
      duration: 'Weeks 7-10',
      activities: [
        'Paid media and UGC campaign launches sequenced by cluster priority',
        'Retail and market center tie-ins for omnichannel reinforcement',
        'Local partner and ambassador recruitment to unlock trust quickly',
        'Experiment matrix to test offers, bundles, and event formats'
      ]
    },
    {
      phase: 'Phase 4: Measurement & Scale',
      duration: 'Ongoing',
      activities: [
        'Cluster dashboards tracking awareness, conversion, and retention signatures',
        'Budget reallocation engine that tilts spend toward outperforming clusters',
        'Localization backlog grooming to continuously refresh creative',
        'Market entry recommendations for the next wave of clusters'
      ]
    }
  ];

  const differentiators = [
    {
      title: 'Human + Data Insights',
      icon: Globe,
      description: 'Our analysts pair quantitative segmentation with on-the-ground interviews so campaigns resonate culturally, not just numerically.'
    },
    {
      title: 'Full-Funnel Localization',
      icon: Share2,
      description: 'Messaging, influencers, media plans, and retail activations all adapt to each cluster—no more copy-paste national campaigns.'
    },
    {
      title: 'Dynamic Cluster Ops',
      icon: Target,
      description: 'Real-time dashboards flag when a cluster is heating up or cooling off, allowing budgets and teams to shift fast.'
    },
    {
      title: 'Cross-Border Expertise',
      icon: MapPin,
      description: 'We specialize in clusters that span borders—think Toronto / Vancouver diasporas or US-Mexico twin cities—so expansion feels native.'
    }
  ];

  const deliverables = [
    'Cluster atlas with data-backed personas, TAM, and competitive landscape',
    'Localization playbooks covering copy, creative, offers, and channel mix',
    'Integrated launch calendar with campaign, retail, and PR touchpoints',
    'Measurement framework with dashboards and decision thresholds'
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center">
              <MapPin size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-5xl font-bold text-[#000000]">Cluster-Based Marketing</h1>
              <p className="text-xl text-gray-600 mt-2">Activate the micro-markets that move the needle</p>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">The Localization Framework</h2>
          <p className="text-lg text-gray-700 mb-8">
            National campaigns miss nuance. We segment demand into actionable clusters and build programs that respect culture, language, and local economics.
          </p>
          <div className="space-y-6">
            {framework.map((phase, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xl">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#000000] mb-2">{phase.phase}</h3>
                    <p className="text-[#38A3A5] font-semibold mb-4">Timeline: {phase.duration}</p>
                  </div>
                </div>
                <ul className="space-y-3 ml-16">
                  {phase.activities.map((activity, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-[#38A3A5] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{activity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">Why Our Clusters Convert</h2>
          <p className="text-lg text-gray-700 mb-8">
            We blend quantitative rigor with cultural intelligence so every micro-market feels like you built the campaign just for them.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {differentiators.map((item, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#000000]">{item.title}</h3>
                </div>
                <p className="text-gray-700">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">Deliverables</h2>
          <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8">
            <ul className="space-y-3">
              {deliverables.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-[#38A3A5] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Let’s Localize for Impact</h2>
          <p className="text-xl mb-8 opacity-90">
            We’ll identify your highest-potential clusters and stand up campaigns that feel native from day one.
          </p>
          <a
            href="mailto:contact@motifunbound.com?subject=Request%20a%20Cluster%20Workshop&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI'd%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
            className="inline-block bg-white text-[#38A3A5] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all"
          >
            Request a Cluster Workshop
          </a>
        </div>
      </div>
    </div>
  );
}
